import React, { useEffect, useRef, useState } from 'react'
import NavigateListCustom from '../../../components/navigation/NavigationListCustom'
import { routeName } from '../../../routes/routeName'
import {
    Backdrop, Box, CircularProgress, Grid, LinearProgress,
    Step, StepContent, StepLabel, Stepper, Tab, Tabs, Typography,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material'
import OptionsButtonCustom from '../../../components/option-button/OptionsButtonCustom';
import FormLesson from './step/form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import { OnlineCourseApi } from '../../../api/cms-online-course/online_course.api';
import { PublishOnlineCourseApi } from '../../../api/publish-online-course/publish-online-course.api';
import { useParams } from 'react-router-dom';
import { dateToView } from '../../../utils/date.utils';
import { alertError, alertSuccess, swalError } from '../../../components/alert/swal';
import ReactPlayer from 'react-player'
import { getFileExtension } from '../../../utils/fileTypeDownload';
import saveAs from 'file-saver';
import { useDispatch } from 'react-redux';
import { setLoading, unsetLoading } from '../../../store/loading/loading.slice';
import { ButtonOutlined, ButtonPrimary } from '../../../components/button/ButtonCustoms';
import { useTranslation } from 'react-i18next'

/* ---------------------------------- Types ---------------------------------- */

type StepType = 'LESSON' | 'QUIZ_BEFORE' | 'QUIZ_AFTER' | 'SURVEY';

interface StepChild {
    label: string;
    type: StepType;
    id?: number;                              // id_online_course_config_form (กรณี quiz/survey)
    id_online_course_study_topics?: number;
    id_online_course_lesson?: number;
    time?: string;
    user_point?: string;                      // เช่น "8/10"
}

interface StepNode extends StepChild {
    children: StepChild[];
}

interface FormAnswer {
    id_online_course_config_form?: number;
    online_course_form_name?: string;
    user_point?: number;
    max_point?: number;
    modified_date?: string;
    config_form?: { online_course_config_form_type?: StepType };
}

/* --------------------------------- Helpers --------------------------------- */

/**
 * ประกอบ URL ไฟล์ให้เป็นรูปแบบเดียวกันทั้งหน้า
 * กันกรณี file_path ลงท้าย / อยู่แล้ว หรือไม่มี / คั่น
 */
const buildFileUrl = (filePath?: string, fileName?: string): string =>
    `${process.env.REACT_APP_FILE_BASE_API}/${filePath ?? ''}/${fileName ?? ''}`
        .replace(/([^:])\/{2,}/g, '$1/');

const formatDuration = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/** หา label คะแนน "user/max" จาก answers ที่โหลดมาแล้ว (ไม่ยิง API ซ้ำ) */
const getPointLabel = (answers: FormAnswer[], idConfigForm?: number): string | undefined => {
    if (!idConfigForm) return undefined;
    const found = answers.find((x) => x?.id_online_course_config_form === idConfigForm);
    return found?.user_point ? `${found.user_point}/${found.max_point}` : undefined;
};

const formatBytes = (bytes: number, decimals = 2): string => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/* ---------------------------- Shared UI components -------------------------- */

const GreenLinearProgress = ({ value }: { value: number }) => (
    <LinearProgress
        variant="determinate"
        color="success"
        value={value}
        sx={{
            margin: '16px 0px',
            height: '10px',
            background: '#E5E5E7',
            borderRadius: '10px',
            '.MuiLinearProgress-bar': {
                background: '#026735',
                borderRadius: '10px',
            },
        }}
    />
);

/* --------------------------------- Component -------------------------------- */

export default function TakeLesson() {
    const { t } = useTranslation();
    const { id } = useParams()
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tabValue, setTabValue] = useState(0);
    const [activeMainStep, setActiveMainStep] = useState(0);
    const [activeSubStep, setActiveSubStep] = useState(0);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    // progress ของวิดีโอ (%) — เตรียมไว้สำหรับบังคับให้ดูจบก่อนกด "ถัดไป"
    const [videoProgress, setVideoProgress] = useState(0);
    const [steps, setSteps] = useState<StepNode[]>([]);
    const [formAnswers, setFormAnswers] = useState<FormAnswer[]>([]);
    const [detail, setDetail] = useState<any>()
    const [basicInfo, setBasicInfo] = useState<any>()
    const [form, setForm] = useState<any>()
    const [items, setItems] = useState<any[]>([])
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [idConfigForm, setIdConfigForm] = useState<number>(0);
    const [file, setFile] = useState<any>();

    /* ------------------------------ Data loading ----------------------------- */

    const loadFormAnswers = async (): Promise<FormAnswer[]> => {
        const res = await PublishOnlineCourseApi.form_answer(id);
        const answers: FormAnswer[] = res.data?.data ?? [];
        setFormAnswers(answers);
        return answers;
    };

    const loadConfigForms = async (): Promise<any[]> => {
        const res = await OnlineCourseApi.getconfigForm({}, id);
        return res.data?.data?.items ?? [];
    };

    const getLessonTopicData = async (idLesson: number, idTopic: number) => {
        const res = await OnlineCourseApi.getLessonTopic(id, idLesson, idTopic);
        setFile(res.data?.data);
    };

    /**
     * โหลดความยาววิดีโอจาก metadata
     * NOTE (แนะนำ): ควรให้ backend เก็บ duration ตอนอัปโหลดไฟล์แล้วส่งมากับ listLessonTopic เลย
     * จะตัด API call + การโหลด metadata ต่อไฟล์ตอนเปิดหน้าออกได้ทั้งหมด
     */
    const getVideoDuration = async (idLesson?: number, idTopic?: number): Promise<number> => {
        try {
            const res = await OnlineCourseApi.getLessonTopic(id, idLesson, idTopic);
            const topicFile = res.data?.data?.online_course_study_topics_file;
            if (!topicFile) return 0;

            const url = buildFileUrl(topicFile.file_path, topicFile.file_name);

            return await new Promise<number>((resolve, reject) => {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => resolve(video.duration);
                video.onerror = () => reject(new Error(`โหลดวิดีโอไม่สำเร็จ: ${url}`)); // i18n-ignore: internal error, only logged via console.error
                video.src = url;
            });
        } catch (err) {
            console.error(err);
            return 0; // ไม่ให้ error หลุดไปพังทั้ง tree
        }
    };

    const loadFormById = async (formId: number) => {
        const res = await OnlineCourseApi.getFormById(formId);
        const data = res.data;

        // flatten choices ของทุก subject ให้เป็น list เดียว
        const merged = (data?.course_form_subject ?? [])
            .flatMap((group: any) =>
                group.choices.map((choice: any) => ({
                    ...choice,
                    online_course_form_choice_type: group.online_course_form_subject_type,
                    online_course_form_subject_point: group.online_course_form_subject_point,
                    online_course_form_choice_point: group.online_course_form_subject_point,
                }))
            )
            .map((item: any, index: number) => ({ ...item, index }));

        setItems(merged);
        setForm(data);
    };

    const loadConfigFormContent = async (idForm: number) => {
        const res = await OnlineCourseApi.getconfigFormById(id, idForm);
        const formId = res.data?.data?.id_online_course_form;
        if (formId) await loadFormById(formId);
    };

    /** โหลดรายละเอียดคอร์ส + เนื้อหาของ step ที่ active อยู่ */
    const loadCourseDetailAndForm = async (stepList: StepNode[], step?: number, subStep?: number) => {
        const res = await PublishOnlineCourseApi.id(id);
        if (res.status >= 300) return;

        const info = res.data.data;
        setDetail(info);
        setBasicInfo(info?.basic_info?.data);

        const mainIndex = step ?? info?.payment?.current_step_main ?? 0;
        const subIndex = subStep ?? info?.payment?.current_step_sub ?? 0;
        setActiveMainStep(mainIndex);
        setActiveSubStep(subIndex);

        const mainNode = stepList[mainIndex];
        const childNode = mainNode?.children?.[subIndex];
        const target = childNode ?? mainNode;

        if (target?.id) {
            setIdConfigForm(target.id);
            await loadConfigFormContent(target.id);
        }
        if (target?.id_online_course_study_topics && target?.id_online_course_lesson) {
            await getLessonTopicData(target.id_online_course_lesson, target.id_online_course_study_topics);
        }
    };

    /** สร้าง tree บทเรียน (quiz ก่อนเรียน → หัวข้อ → quiz หลังเรียน) โดยใช้ answers ที่โหลดมาแล้ว */
    const loadLessonTree = async (configForms: any[], answers: FormAnswer[]): Promise<StepNode[]> => {
        const res = await OnlineCourseApi.listLesson(id);
        const lessons = res.data?.data ?? [];

        return Promise.all(
            lessons.map(async (lesson: any): Promise<StepNode> => {
                const topicRes = await OnlineCourseApi.listLessonTopic(id, lesson.id_online_course_lesson);
                const topics = topicRes?.data?.data ?? [];

                const quizOfType = (type: StepType): StepChild[] =>
                    configForms
                        .filter((x: any) =>
                            x.online_course_config_form_type === type &&
                            topics.some((y: any) => y?.id_online_course_study_topics === x?.id_online_course_study_topics)
                        )
                        .map((f: any) => ({
                            label: f.online_course_form_name,
                            id: f?.id_online_course_config_form,
                            type,
                            user_point: getPointLabel(answers, f?.id_online_course_config_form),
                        }));

                const topicChildren = await Promise.all(
                    topics.map(async (t: any): Promise<StepChild> => {
                        const seconds = await getVideoDuration(
                            t?.id_online_course_lesson,
                            t?.id_online_course_study_topics
                        );
                        return {
                            label: t.online_course_study_topics_name,
                            id_online_course_study_topics: t?.id_online_course_study_topics,
                            id_online_course_lesson: t?.id_online_course_lesson,
                            time: formatDuration(seconds),
                            type: 'LESSON',
                            user_point: getPointLabel(answers, t?.id_online_course_config_form),
                        };
                    })
                );

                return {
                    label: lesson.online_course_lesson_name,
                    type: 'LESSON',
                    children: [
                        ...quizOfType('QUIZ_BEFORE'),
                        ...topicChildren,
                        ...quizOfType('QUIZ_AFTER'),
                    ],
                };
            })
        );
    };

    /** merge steps: QUIZ_BEFORE → lessons → QUIZ_AFTER → SURVEY (เฉพาะที่ไม่ผูกกับหัวข้อ) */
    const transformConfigToSteps = (
        configItems: any[],
        lessonItems: StepNode[],
        answers: FormAnswer[]
    ): StepNode[] => {
        const createNodes = (type: StepType): StepNode[] =>
            configItems
                .filter((x: any) => x.online_course_config_form_type === type && !x.id_online_course_study_topics)
                .map((item: any) => ({
                    id: item.id_online_course_config_form,
                    label: item.online_course_form_name,
                    type: item.type,
                    children: [],
                    user_point: getPointLabel(answers, item.id_online_course_config_form),
                }));

        return [
            ...createNodes('QUIZ_BEFORE'),
            ...lessonItems,
            ...createNodes('QUIZ_AFTER'),
            ...createNodes('SURVEY'),
        ];
    };

    const init = async () => {
        setIsLoading(true);
        try {
            // โหลด config + answers พร้อมกัน (answers โหลดครั้งเดียว ใช้ lookup ทั้ง tree)
            const [configItems, answers] = await Promise.all([loadConfigForms(), loadFormAnswers()]);
            const lessonTree = await loadLessonTree(configItems, answers);
            const mergedSteps = transformConfigToSteps(configItems, lessonTree, answers);
            setSteps(mergedSteps);
            await loadCourseDetailAndForm(mergedSteps);
        } catch (err) {
            console.error(err);
            swalError(t("โหลดข้อมูลบทเรียนไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* ------------------------------- Navigation ------------------------------ */

    const handleHTML5TimeUpdate = () => {
        const video = videoRef.current;
        if (video && video.duration > 0) {
            setVideoProgress((video.currentTime / video.duration) * 100);
        }
    };

    const handleUpdateStep = async (mainStep: number, subStep: number) => {
        try {
            const res = await PublishOnlineCourseApi.update_step(id, {
                current_step_main: mainStep,
                current_step_sub: subStep,
            });
            if (res.status < 300 && mainStep === steps.length) {
                // เรียนครบทุก step แล้ว → ตัดเกรด
                const gradingRes = await PublishOnlineCourseApi.grading(id);
                if (gradingRes.data.data) {
                    alertSuccess(
                        t("ยินดีด้วย"),
                        t("ท่านดำเนินการ เรียนจบคอร์สตามเกณฑ์ที่กำหนดแล้ว ท่านสามารถดาวน์โหลดวุฒิบัตรได้ที่เมนู ผลการเรียน"),
                        undefined,
                        () => window.location.reload()
                    );
                } else {
                    alertError(
                        t("เสียใจด้วย"),
                        t("มีคอร์สที่ไม่เป็นตามเกณฑ์ที่กำหนดแล้ว"),
                        undefined,
                        () => window.location.reload()
                    );
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    /** คำนวณ step ถัดไปจาก state ปัจจุบัน */
    const getNextStep = (): { main: number; sub: number } => {
        const currentChildren = steps[activeMainStep]?.children ?? [];
        if (currentChildren.length > 0 && activeSubStep < currentChildren.length - 1) {
            return { main: activeMainStep, sub: activeSubStep + 1 };
        }
        return { main: activeMainStep + 1, sub: 0 };
    };

    const handleNext = async () => {
        setFile(null);
        setSelectedIndex(0);
        const next = getNextStep();
        setActiveMainStep(next.main);
        setActiveSubStep(next.sub);
        await handleUpdateStep(next.main, next.sub);
        await loadCourseDetailAndForm(steps, next.main, next.sub);
    };

    const handleBack = async () => {
        let main = activeMainStep;
        let sub = activeSubStep;

        if (activeSubStep > 0) {
            sub = activeSubStep - 1;
        } else if (activeMainStep > 0) {
            main = activeMainStep - 1;
            const prevChildren = steps[main]?.children ?? [];
            sub = prevChildren.length > 0 ? prevChildren.length - 1 : 0;
        } else {
            return; // อยู่ step แรกสุดแล้ว
        }

        setFile(null);
        setSelectedIndex(0);
        setActiveMainStep(main);
        setActiveSubStep(sub);
        // โหลดเนื้อหาของ step ที่ย้อนกลับไปด้วย (เดิมเปลี่ยนแค่ index เนื้อหาไม่เปลี่ยนตาม)
        await loadCourseDetailAndForm(steps, main, sub);
    };

    /* -------------------------------- Actions -------------------------------- */

    const handleSubmit = async () => {
        const body = {
            id_online_course_config_form: idConfigForm,
            id_online_course_form: form?.id_online_course_form,
            online_course_form_name: form?.online_course_form_name,
            online_course_form_answer_status: 'active',
            answer_detail: items?.map((item) => ({
                id_online_course_form_subject: item?.id_online_course_form_subject,
                online_course_form_subject: item?.online_course_form_subject,
                online_course_form_subject_point: item?.online_course_form_subject_point,
                id_online_course_form_choice: item?.id_online_course_form_choice,
                online_course_form_choice: item?.online_course_form_choice,
                online_course_form_choice_true: item?.online_course_form_choice_true,
                online_course_form_choice_point: item?.online_course_form_choice_point,
                is_true: item?.is_true,
                online_course_form_answer_detail: item?.online_course_form_answer_detail,
            })),
        };

        try {
            const res = await PublishOnlineCourseApi.post_form_answer(id, body);
            if (res.status < 300) {
                setForm(null);
                // อัปเดต step บน server ก่อน แล้วค่อย init ใหม่
                // → loadCourseDetailAndForm จะอ่าน current_step จาก server ได้ค่าที่ถูกต้อง ไม่มี race condition
                const next = getNextStep();
                await handleUpdateStep(next.main, next.sub);
                await init();
                setSelectedIndex(0)
            }
        } catch (error: any) {
            console.error(error);
            // บันทึกไม่สำเร็จ → แจ้ง user และ "ไม่" เลื่อนไป step ถัดไป (คำตอบจะได้ไม่หายเงียบ ๆ)
            swalError(error?.response?.data?.message || t("บันทึกคำตอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
        }
    };

    const handleDownloadFile = async () => {
        dispatch(setLoading());
        try {
            const res = await PublishOnlineCourseApi.my_online_course_diploma_by_id(id);
            if (res.status < 300) {
                const contentType = res.headers['content-type'];
                const blob = new Blob([res.data], { type: contentType });
                const fileExtension = getFileExtension(contentType || '');
                saveAs(blob, `diploma${new Date().getTime()}.${fileExtension}`);
            }
        } catch (error) {
            console.error(error);
            swalError(t("ดาวน์โหลดวุฒิบัตรไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
        } finally {
            dispatch(unsetLoading());
        }
    };

    /* --------------------------------- Render -------------------------------- */

    const isLearning = ['CANCEL', 'LEARNING'].includes(detail?.payment?.online_course_learning_status);
    const isLastSubjectPage = form && (form?.course_form_subject?.length - 1) === selectedIndex;

    if (isLoading) {
        return (
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 9999 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <div className="h-auto">
            <NavigateListCustom
                navigateData={[
                    {
                        name: t("ระบบคอร์สออนไลน์"),
                        key: '1',
                        pathName: routeName.students.OnlineCourse,
                    },
                    {
                        name: t("คอร์สทั้งหมด"),
                        key: '2',
                        pathName: routeName.students.ListOnlineCourse,
                    },
                    {
                        name: `${basicInfo?.online_course_no} ${basicInfo?.online_course_name}`,
                        key: '3',
                        pathName: routeName.students.OnlineCourseDetail.replace(':id', `${id}`),
                    },
                ]}
            />
            <section className="my-4">
                <Typography variant="h3">
                    {basicInfo?.online_course_no} {basicInfo?.online_course_name}
                </Typography>
            </section>
            <section className="my-4">
                {!isLearning && (
                    <Typography sx={{ marginBottom: '10px' }}>
                        {t("วันที่เริ่มเรียน :")} {dateToView(formAnswers[0]?.modified_date)}{' '}
                        {t("สถานะ :")} {detail?.payment?.online_course_learning_status === 'PASSED' ? t("จบคอร์ส") : t("ไม่ผ่าน")}{' '}
                        {t("วันที่เรียนจบ :")} {dateToView(formAnswers[formAnswers.length - 1]?.modified_date)}
                    </Typography>
                )}
                <Grid container spacing={2}>
                    {/* ----------------------- ฝั่งซ้าย: เนื้อหา ----------------------- */}
                    <Grid item xs={12} lg={7}>
                        <div
                            style={{
                                boxShadow: '0px 0px 8.8px 0px #00000026',
                                padding: '40px',
                                borderRadius: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                                marginBottom: '20px',
                            }}
                        >
                            {isLearning ? (
                                <>
                                    {/* เนื้อหา: ฟอร์ม หรือ วิดีโอ */}
                                    {!file ? (
                                        form && (
                                            <FormLesson
                                                form={form}
                                                items={items}
                                                setItems={setItems}
                                                selectedIndex={selectedIndex}
                                                setSelectedIndex={setSelectedIndex}
                                            />
                                        )
                                    ) : file?.online_course_study_topics_file ? (
                                        <video
                                            ref={videoRef}
                                            width="100%"
                                            controls
                                            onTimeUpdate={handleHTML5TimeUpdate}
                                        >
                                            <source
                                                src={buildFileUrl(
                                                    file?.online_course_study_topics_file?.file_path,
                                                    file?.online_course_study_topics_file?.file_name
                                                )}
                                                type="video/mp4"
                                            />
                                            Your browser does not support HTML5 video.
                                        </video>
                                    ) : (
                                        <ReactPlayer
                                            src={file?.online_course_study_topics_link}
                                            controls
                                            width="100%"
                                            height="457px"
                                        />
                                    )}

                                    {/* ปุ่มนำทาง */}
                                    {!file && isLastSubjectPage ? (
                                        // หน้าสุดท้ายของแบบทดสอบ → ยืนยันส่งคำตอบ
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {selectedIndex !== 0 && (
                                                <ButtonOutlined
                                                    onClick={() => setSelectedIndex(selectedIndex - 1)}
                                                    buttonName={t("ย้อนกลับ")}
                                                    btnProps={{ width: '100%' }}
                                                />
                                            )}
                                            <ButtonPrimary
                                                onClick={handleSubmit}
                                                buttonName={t("ยืนยัน")}
                                                btnProps={{ width: '100%' }}
                                            />
                                        </div>
                                    ) : file ? (
                                        // เนื้อหาวิดีโอ → ไป step ถัดไป
                                        // หากต้องการบังคับให้ดูวิดีโอจบก่อน: เพิ่ม disabled={videoProgress < 100}
                                        <ButtonPrimary
                                            mode="next"
                                            onClick={handleNext}
                                            buttonName={t("ถัดไป")}
                                            btnProps={{ width: '100%' }}
                                        />
                                    ) : (
                                        // อยู่ระหว่างทำแบบทดสอบ → เลื่อนหน้าคำถาม
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {selectedIndex !== 0 && (
                                                <ButtonOutlined
                                                    onClick={() => setSelectedIndex(selectedIndex - 1)}
                                                    buttonName={t("ย้อนกลับ")}
                                                    btnProps={{ width: '100%' }}
                                                />
                                            )}
                                            <ButtonPrimary
                                                mode="next"
                                                onClick={() => setSelectedIndex(selectedIndex + 1)}
                                                buttonName={t("ถัดไป")}
                                                btnProps={{ width: '100%' }}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* เรียนจบแล้ว → สรุปบทเรียน */
                                <div>
                                    <Typography variant="h5">{t("บทเรียน")}</Typography>
                                    <Typography>
                                        {steps.filter((x) => x.type === 'LESSON').length} {t("บท")}
                                    </Typography>
                                    {steps
                                        .filter((x) => x.type === 'LESSON')
                                        .map((item, lessonIndex) => (
                                            <React.Fragment key={`lesson-${lessonIndex}-${item.label}`}>
                                                <Accordion
                                                    elevation={0}
                                                    disableGutters
                                                    defaultExpanded
                                                    sx={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                        '&::before': { display: 'none' },
                                                    }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            paddingX: 0,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: '10px',
                                                            }}
                                                        >
                                                            <Typography sx={{ marginLeft: '15px', fontWeight: '600' }}>
                                                                {item.label}
                                                            </Typography>
                                                            <div style={{ width: '50%' }}>
                                                                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <div style={{ width: '90%' }}>
                                                                        <GreenLinearProgress value={100} />
                                                                    </div>
                                                                    <Typography>100%</Typography>
                                                                </Box>
                                                            </div>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ backgroundColor: 'transparent', paddingX: 0 }}>
                                                        {item.children
                                                            .filter((x) => x.type === 'LESSON')
                                                            .map((x, topicIndex) => (
                                                                <Box
                                                                    key={x.id_online_course_study_topics ?? `topic-${topicIndex}`}
                                                                    sx={{
                                                                        width: '100%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: '10px',
                                                                    }}
                                                                >
                                                                    <Typography sx={{ marginLeft: '15px' }}>{x.label}</Typography>
                                                                    <div style={{ width: '50%' }}>
                                                                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            <div style={{ width: '90%' }}>
                                                                                <GreenLinearProgress value={100} />
                                                                            </div>
                                                                            <Typography>100%</Typography>
                                                                        </Box>
                                                                    </div>
                                                                </Box>
                                                            ))}
                                                    </AccordionDetails>
                                                </Accordion>
                                                <Box sx={{ borderBottom: '1px solid #DDE1EB' }} />
                                            </React.Fragment>
                                        ))}
                                </div>
                            )}
                        </div>
                    </Grid>

                    {/* ----------------------- ฝั่งขวา: sidebar ----------------------- */}
                    <Grid item xs={12} lg={5}>
                        <div
                            style={{
                                boxShadow: '0px 0px 8.8px 0px #00000026',
                                borderRadius: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                                marginBottom: '20px',
                            }}
                        >
                            {isLearning && (
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={(_e, newValue: number) => setTabValue(newValue)}
                                        aria-label={t("แท็บข้อมูลบทเรียน")}
                                        sx={{ '& .MuiTabs-indicator': { backgroundColor: '#F79009' } }}
                                    >
                                        <Tab label={t("เนื้อหา")} sx={{ '&.Mui-selected': { color: '#F79009' } }} />
                                        <Tab label={t("เอกสาร")} sx={{ '&.Mui-selected': { color: '#F79009' } }} />
                                        <Tab label={t("คำศัพท์")} sx={{ '&.Mui-selected': { color: '#F79009' } }} />
                                    </Tabs>
                                </Box>
                            )}

                            {tabValue === 0 && (
                                <div style={{ padding: '20px 40px' }}>
                                    <Box sx={{ maxWidth: 600 }}>
                                        {isLearning ? (
                                            <Stepper
                                                activeStep={activeMainStep}
                                                orientation="vertical"
                                                nonLinear
                                                sx={{
                                                    '& .Mui-active': { color: '#F79009 !important' },
                                                    '& .MuiStepIcon-text': { display: 'none' },
                                                    // '& > .MuiStepConnector-root': { display: 'none' },
                                                }}
                                            >
                                                {steps.map((step, index) => (
                                                    <Step
                                                        key={`step-${index}-${step.label}`}
                                                        expanded
                                                        // nonLinear ทำให้ MUI ไม่ตั้ง completed อัตโนมัติ → ตั้งเองเพื่อให้ขึ้นเครื่องหมายถูก
                                                        completed={activeMainStep > index}
                                                    >
                                                        <StepLabel
                                                            sx={{
                                                                '& .MuiStepLabel-iconContainer': {
                                                                    display: step.children.length > 0 ? 'none' : 'block',
                                                                },
                                                                // ไอคอน ✓ ของ step ที่เรียนจบแล้ว → สีเขียว
                                                                '& .MuiStepIcon-root.Mui-completed': {
                                                                    color: '#0F9F0C',
                                                                },
                                                            }}
                                                        >
                                                            {step.children.length === 0 ? (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <Typography>{step.label}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {t("คะแนน")} {step.user_point || '-'}
                                                                    </Typography>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <Typography fontWeight="bold">{step.label}</Typography>
                                                                    <Typography sx={{ fontSize: '12px' }}>
                                                                        {step.children.length} {t("หัวข้อการเรียน")}
                                                                    </Typography>
                                                                </div>
                                                            )}
                                                        </StepLabel>
                                                        <StepContent
                                                            sx={{ border: step.children.length > 0 ? '0px' : undefined }}
                                                        >
                                                            {step.children.length > 0 && (
                                                                <Stepper
                                                                    activeStep={activeMainStep === index ? activeSubStep : 9999}
                                                                    orientation="vertical"
                                                                    nonLinear
                                                                    sx={{
                                                                        '& .Mui-active': { color: '#F79009 !important' },
                                                                        '& .MuiStepIcon-text': { display: 'none' },
                                                                    }}
                                                                >
                                                                    {step.children.map((child, childIndex) => {
                                                                        const isChildCompleted =
                                                                            activeMainStep > index ||
                                                                            (activeMainStep === index && activeSubStep > childIndex);
                                                                        return (
                                                                            <Step
                                                                                key={`child-${childIndex}-${child.label}`}
                                                                                completed={isChildCompleted}
                                                                            >
                                                                                <StepLabel
                                                                                    sx={{
                                                                                        '& .Mui-active': { color: '#F79009' },
                                                                                        '& .MuiStepIcon-text': { display: 'none' },
                                                                                        // ไอคอน ✓ ของหัวข้อที่เรียนจบแล้ว → สีเขียว
                                                                                        '& .MuiStepIcon-root.Mui-completed': {
                                                                                            color: '#0F9F0C',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                    <Typography>{child.label}</Typography>
                                                                                    {!child?.time ? (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            {t("คะแนน")} {child.user_point}
                                                                                        </Typography>
                                                                                    ) : (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            {child.time}
                                                                                        </Typography>
                                                                                    )}
                                                                                </div>
                                                                                </StepLabel>
                                                                            </Step>
                                                                        );
                                                                    })}
                                                                </Stepper>
                                                            )}
                                                        </StepContent>
                                                    </Step>
                                                ))}
                                            </Stepper>
                                        ) : (
                                            /* เรียนจบแล้ว → สรุปผลการเรียน */
                                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <img
                                                    src={buildFileUrl(
                                                        basicInfo?.cover_image?.file_path,
                                                        basicInfo?.cover_image?.file_name
                                                    )}
                                                    alt={basicInfo?.online_course_name || t("ภาพปกคอร์ส")}
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                    }}
                                                />
                                                <div style={{ marginTop: '20px' }}>
                                                    <Typography>{t("ความคืบหน้าการเรียน")}</Typography>
                                                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div style={{ width: '90%' }}>
                                                            <GreenLinearProgress value={100} />
                                                        </div>
                                                        <Typography sx={{ fontSize: '14px', color: '#0F9F0C' }}>100%</Typography>
                                                    </Box>
                                                </div>
                                                {formAnswers.map((item, answerIndex) => (
                                                    <div key={item.id_online_course_config_form ?? `answer-${answerIndex}`}>
                                                        <Typography>{item.online_course_form_name}</Typography>
                                                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <div style={{ width: '85%' }}>
                                                                <GreenLinearProgress
                                                                    value={
                                                                        item.config_form?.online_course_config_form_type === 'SURVEY'
                                                                            ? 100
                                                                            : ((item.user_point ?? 0) * 100) / (item.max_point || 1)
                                                                    }
                                                                />
                                                            </div>
                                                            {item.config_form?.online_course_config_form_type === 'SURVEY' ? (
                                                                <Typography sx={{ fontSize: '14px', color: '#0F9F0C' }}>{t("ทำแล้ว")}</Typography>
                                                            ) : (
                                                                <Typography>
                                                                    {item.user_point} / {item.max_point}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </div>
                                                ))}

                                                {detail?.payment?.online_course_learning_status === 'PASSED' && (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '10px',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <StarIcon sx={{ color: '#F79009' }} />
                                                            <Typography variant="h5">{t("วุฒิบัตร")}</Typography>
                                                        </div>
                                                        <Typography
                                                            sx={{ textDecoration: 'underline', color: '#0F9F0C', cursor: 'pointer' }}
                                                            onClick={handleDownloadFile}
                                                        >
                                                            {t("ดาวน์โหลด")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </div>
                            )}
                            {tabValue === 1 && <DocumentLesson />}
                            {tabValue === 2 && <TerminologyLesson />}
                        </div>
                    </Grid>
                </Grid>
            </section>
        </div>
    );
}

/* ----------------------------- Sub components ------------------------------ */

export const DocumentLesson = () => {
    const { t } = useTranslation();
    const { id } = useParams()
    const [data, setData] = useState<any[]>([])

    useEffect(() => {
        const getDocuments = async () => {
            try {
                const res = await OnlineCourseApi.listFile({}, id);
                // เดิมเช็ค res.data?.status ซึ่งเป็น body ไม่ใช่ HTTP status
                if (res.status < 300) {
                    setData(res.data?.data?.items ?? []);
                }
            } catch (err) {
                console.error(err);
            }
        };
        getDocuments();
    }, [id])

    const openFile = async (idFile: number) => {
        try {
            const res = await OnlineCourseApi.getFileById(id, idFile);
            if (res.status < 300) {
                window.open(buildFileUrl(res.data?.data?.file_path, res.data?.data?.file_name), '_blank');
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div style={{ padding: '20px 40px' }}>
            <Typography sx={{ fontWeight: '600' }}>{t("เอกสารประกอบ")}</Typography>
            {data.map((item: any) => (
                <div
                    key={item?.id_online_course_file}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e7e7ee',
                        alignItems: 'center',
                        marginBottom: '5px',
                    }}
                >
                    <Typography
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: '1',
                            overflow: 'hidden',
                        }}
                    >
                        {item?.file_name} {formatBytes(item?.file_size)} {dateToView(item?.modified_date)}
                    </Typography>
                    <OptionsButtonCustom
                        enableDownload
                        onDownloadClick={() => openFile(item?.id_online_course_file)}
                    />
                </div>
            ))}
        </div>
    )
}

export const TerminologyLesson = () => {
    const { id } = useParams()
    const [data, setData] = useState<any[]>([])

    useEffect(() => {
        const getTerminology = async () => {
            try {
                const res = await OnlineCourseApi.listTerminology({}, id);
                // เดิมเช็ค res.data?.status ซึ่งเป็น body ไม่ใช่ HTTP status
                if (res.status < 300) {
                    setData(res.data?.data?.items ?? []);
                }
            } catch (err) {
                console.error(err);
            }
        };
        getTerminology();
    }, [id])

    return (
        <div style={{ padding: '20px 40px' }}>
            {data.map((item: any, index: number) => (
                <div
                    key={item?.id_online_course_terminology ?? `term-${index}`}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        borderBottom: '1px solid #e7e7ee',
                        marginBottom: '5px',
                    }}
                >
                    <Typography sx={{ fontWeight: '600' }}>
                        {index + 1}. {item?.online_course_terminology}
                    </Typography>
                    <Typography>{item?.online_course_terminology_desc}</Typography>
                </div>
            ))}
        </div>
    )
}